import React, { useEffect, useMemo, useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { FiFileText, FiGrid, FiPrinter } from "react-icons/fi";
import { useReportExport } from "../hooks/useReportExport";
import { escapeCsv } from "@/utils/reportCsv";
import { CertStatusFilterDropdown } from "../reports/CertStatusFilterDropdown";
import { PaginationControls } from "../reports/PaginationControls";
import { useCertificationCycleStatus, useCoreData } from "@/queries.ts";
import { normalizeStatus } from "@/utils/certifications";
import { CertificationCycleStatusRow } from "@/models/file-submission.tsx";
import { ReportingCalendarModel } from "@/models";
import { isAdminBuild } from "@/utils/isAdminBuild";

export type CertStatus =
  | "Not Certified"
  | "Uncertified"
  | "Certified"
  | "Decertified";

export const ALL_STATUSES: CertStatus[] = [
  "Not Certified",
  "Uncertified",
  "Certified",
  "Decertified",
];

export interface CertCycleStatusRow {
  esiid: number;
  leaName: string;
  vendorNumber: string;
  status: CertStatus;
  certifierFirstName: string;
  certifierLastName: string;
  certifierEmail: string;
  certifierPhoneNumber: string;
  certifierExtension: string | null;
  totalEnrollment: number;
}

function cycleLabel(reportingCalendarId: string, windowStart: string): string {
  const m = reportingCalendarId.match(/^MNTH(\d{4})(\d{2})$/);
  if (m) {
    const [, year, month] = m;
    return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
      "en-US",
      { month: "long", year: "numeric" }
    );
  }
  const a = reportingCalendarId.match(/^ANNL(\d{4})/);
  if (a) return `Annual ${a[1]}`;
  return windowStart
    ? new Date(windowStart + "T00:00:00").toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : reportingCalendarId;
}

function toLocalMidnight(iso: string): Date {
  const [y, m, d] = iso.split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, d);
}

export interface CycleOption {
  value: string;
  label: string;
}

export function getCycleOptions(
  reportingCalendar: ReportingCalendarModel[]
): CycleOption[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return reportingCalendar
    .filter(
      (c) =>
        c.CAPSDACsnpshtCode.startsWith("MNTH") &&
        toLocalMidnight(c.WindowStart) <= today
    )
    .sort(
      (a, b) =>
        toLocalMidnight(b.AmendmentEnd ?? b.WindowStart).getTime() -
        toLocalMidnight(a.AmendmentEnd ?? a.WindowStart).getTime()
    )
    .map((c) => ({
      value: c.CAPSDACsnpshtCode,
      label: cycleLabel(c.CAPSDACsnpshtCode, c.WindowStart),
    }));
}

export const STATUS_COLORS: Record<CertStatus, React.CSSProperties> = {
  "Not Certified": {
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    border: "1px solid #fecaca",
  },
  Uncertified: {
    color: "#92400e",
    backgroundColor: "#fef3c7",
    border: "1px solid #fde68a",
  },
  Certified: {
    color: "#065f46",
    backgroundColor: "#d1fae5",
    border: "1px solid #a7f3d0",
  },
  Decertified: {
    color: "#92400e",
    backgroundColor: "#fef3c7",
    border: "1px solid #fde68a",
  },
};

export const STATUS_DOT: Record<CertStatus, string> = {
  "Not Certified": "#dc2626",
  Uncertified: "#d97706",
  Certified: "#059669",
  Decertified: "#d97706",
};

export function mapCertStatus(rawStatus: string): CertStatus {
  const normalized = normalizeStatus(rawStatus);
  if (normalized === "Closed") return "Not Certified";
  if (
    normalized === "Uncertified" ||
    normalized === "Certified" ||
    normalized === "Decertified"
  ) {
    return normalized;
  }
  return "Uncertified";
}

export function toCertCycleStatusRows(
  rows: CertificationCycleStatusRow[]
): CertCycleStatusRow[] {
  return rows.map((r) => ({
    esiid: r.ESIID,
    leaName: r.LEAName,
    vendorNumber: r.VendorNumber,
    status: mapCertStatus(r.Status),
    certifierFirstName: r.CertifierFirstName ?? "",
    certifierLastName: r.CertifierLastName ?? "",
    certifierEmail: r.CertifierEmail ?? "",
    certifierPhoneNumber: r.CertifierPhoneNumber ?? "",
    certifierExtension: r.CertifierExtension,
    totalEnrollment: r.TotalEnrollment,
  }));
}

export function formatCertifierName(row: CertCycleStatusRow): string {
  return `${row.certifierFirstName} ${row.certifierLastName}`.trim();
}

export function formatCertifierPhone(row: CertCycleStatusRow): string {
  if (!row.certifierPhoneNumber) return "";
  return row.certifierExtension
    ? `${row.certifierPhoneNumber} x${row.certifierExtension}`
    : row.certifierPhoneNumber;
}

export function sumEnrollment(rows: CertCycleStatusRow[]): number {
  return rows.reduce((acc, r) => acc + r.totalEnrollment, 0);
}

export function toExportRows(rows: CertCycleStatusRow[]) {
  return rows.map((r) => ({
    leaName: r.leaName,
    vendorNumber: r.vendorNumber,
    status: r.status,
    certifierName: formatCertifierName(r),
    certifierEmail: r.certifierEmail,
    certifierPhone: formatCertifierPhone(r),
    totalEnrollment: r.totalEnrollment,
  }));
}

const CSV_HEADERS = [
  { label: "LEA Name", key: "leaName" },
  { label: "Vendor Number", key: "vendorNumber" },
  { label: "Certification Status", key: "status" },
  { label: "Certifier Name", key: "certifierName" },
  { label: "Certifier Email", key: "certifierEmail" },
  { label: "Certifier Phone", key: "certifierPhone" },
  { label: "Total Enrollment", key: "totalEnrollment" },
] as const;

const HEADER_ROW = "var(--color-p2)";
const ROW_BORDER = "1px solid #dee2e6";
const STRIPE_BG = "#f6f8fa";
const HOVER_BG = "#eef2f6";

const labelStyle: React.CSSProperties = {
  fontSize: "0.72rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#374151",
  fontWeight: 700,
  marginBottom: "4px",
  display: "block",
};

const BTN_STYLE: React.CSSProperties = {
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
};

const thBase: React.CSSProperties = {
  color: "#ffffff",
  fontWeight: 600,
  padding: "10px 12px",
  border: "none",
  backgroundColor: HEADER_ROW,
  fontSize: "0.8125rem",
  verticalAlign: "bottom",
};
const thLeft: React.CSSProperties = { ...thBase, textAlign: "left" };
const thRight: React.CSSProperties = { ...thBase, textAlign: "right" };

const tdBase: React.CSSProperties = {
  padding: "10px 12px",
  borderBottom: ROW_BORDER,
  fontSize: "0.8125rem",
  color: "#0f1923",
  fontWeight: 500,
  verticalAlign: "middle",
};
const tdRight: React.CSSProperties = {
  ...tdBase,
  textAlign: "right",
  fontWeight: 500,
};
const tdMuted: React.CSSProperties = { ...tdBase, color: "#94a3b8" };

const tdWrap: React.CSSProperties = {
  ...tdBase,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};
const nowrapSpan: React.CSSProperties = { whiteSpace: "nowrap" };

const tdTotalLabel: React.CSSProperties = {
  ...tdBase,
  fontWeight: 700,
  borderTop: `2px solid ${HEADER_ROW}`,
  borderBottom: "none",
};
const tdTotalRight: React.CSSProperties = {
  ...tdTotalLabel,
  textAlign: "right",
};

const badgeStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "3px 9px",
  borderRadius: "999px",
  fontSize: "0.72rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const PAGE_SIZE = 25;

export function Report2_1() {
  const { reportContentRef, exportToPDF, handlePrint } = useReportExport();

  const coreData = useCoreData();
  const cycleOptions = getCycleOptions(coreData.reportingCalendar);

  const [selectedCycle, setSelectedCycle] = useState(
    () => cycleOptions[0]?.value ?? ""
  );
  const selectedCycleLabel =
    cycleOptions.find((c) => c.value === selectedCycle)?.label ?? "";

  const isAdmin = isAdminBuild();
  const result = useCertificationCycleStatus(selectedCycle, isAdmin);
  const data = useMemo(
    () => toCertCycleStatusRows(result.data ?? []),
    [result.data]
  );

  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(
    () => new Set(ALL_STATUSES)
  );
  const toggleStatus = (status: string) =>
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });
  const selectAll = () => setSelectedStatuses(new Set(ALL_STATUSES));
  const deselectAll = () => setSelectedStatuses(new Set());
  const isFiltered = selectedStatuses.size < ALL_STATUSES.length;

  const filteredData = data
    .filter((row) => selectedStatuses.has(row.status))
    .sort((a, b) => a.leaName.localeCompare(b.leaName));
  const totalEnrollment = sumEnrollment(filteredData);

  const [currentPage, setCurrentPage] = useState(1);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [pageInput, setPageInput] = useState("1");

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCycle, selectedStatuses]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedData = filteredData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    if (!Number.isNaN(parsed)) {
      setCurrentPage(Math.min(totalPages, Math.max(1, parsed)));
    } else {
      setPageInput(String(currentPage));
    }
  };

  const displayedData = isExportingAll ? filteredData : pagedData;

  const baseFilename = `report-2.1-${selectedCycle}`;

  const runWithFullData = (action: () => void) => {
    setIsExportingAll(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        action();
        setTimeout(() => setIsExportingAll(false), 1000);
      });
    });
  };

  const handleExportPDF = () =>
    runWithFullData(() =>
      exportToPDF({
        fileName: `${baseFilename}.pdf`,
        exportWidth: 1040,
        scale: 0.72,
        hideNoPrint: true,
        expandScrollSelector: ".table-responsive",
      })
    );

  const handlePrintClick = () => runWithFullData(handlePrint);

  const handleExportCSV = () => {
    const rows = toExportRows(filteredData);
    const headerLine = CSV_HEADERS.map((h) => escapeCsv(h.label)).join(",");
    const dataLines = rows.map((row) =>
      CSV_HEADERS.map((h) =>
        escapeCsv(String((row as Record<string, unknown>)[h.key] ?? ""))
      ).join(",")
    );
    const csv = "\uFEFF" + [headerLine, ...dataLines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseFilename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div>
      <style>{`
        @page { size: A4 landscape; margin: 1.5cm; }
        @media print {
          .report-filter-no-print { display: none !important; }
          body * { visibility: hidden !important; }
          .report-print-area, .report-print-area * { visibility: visible !important; }
          .report-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
          }
          .report-print-area .table-responsive {
            max-height: none !important;
            overflow: visible !important;
          }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div
        className="report-filter-no-print d-flex align-items-end gap-3 mb-4 flex-wrap"
        style={{ rowGap: "12px" }}
      >
        <div style={{ minWidth: "220px" }}>
          <label style={labelStyle} htmlFor="cycle-select">
            Submission Cycle
          </label>
          <select
            id="cycle-select"
            className="form-select"
            style={{
              fontSize: "0.925rem",
              color: "#0f1923",
              fontWeight: 500,
              cursor: "pointer",
            }}
            value={selectedCycle}
            onChange={(e) => setSelectedCycle(e.target.value)}
          >
            {cycleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ minWidth: "240px" }}>
          <label style={labelStyle}>Certification Status</label>
          <CertStatusFilterDropdown
            allStatuses={ALL_STATUSES}
            selectedStatuses={selectedStatuses}
            onToggle={toggleStatus}
            onSelectAll={selectAll}
            onDeselectAll={deselectAll}
            isFiltered={isFiltered}
            statusColors={STATUS_DOT}
          />
        </div>
      </div>

      <div ref={reportContentRef} className="report-print-area">
        <div className="d-flex align-items-start justify-content-between mb-4 gap-3">
          <div>
            <h2
              className="fw-bold mb-1"
              style={{ fontSize: "1.05rem", color: "#1a1a2e", lineHeight: 1.4 }}
            >
              State-Level Report: 2.1 – Certification Cycle Status
            </h2>
            <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
              Submission Cycle: <strong>{selectedCycleLabel}</strong>
            </p>
          </div>
          <div className="report-filter-no-print d-flex gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handleExportPDF}
            >
              <FiFileText size={15} /> PDF
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handleExportCSV}
            >
              <FiGrid size={15} /> CSV
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              style={BTN_STYLE}
              onClick={handlePrintClick}
            >
              <FiPrinter size={15} /> Print
            </button>
          </div>
        </div>

        {result.isLoading ? (
          <div className="p-4 text-muted" role="status" aria-live="polite">
            Loading certification cycle status…
          </div>
        ) : result.isError ? (
          <div className="p-4 text-danger" role="alert">
            Failed to load certification cycle status
            {result.error instanceof Error ? `: ${result.error.message}` : "."}
          </div>
        ) : (
          <>
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <div
                className="table-responsive"
                style={{
                  maxHeight: "560px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: "920px",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "11%" }} />
                  </colgroup>
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr className="report-table-header">
                      <th style={thLeft}>LEA Name</th>
                      <th style={thLeft}>Vendor Number</th>
                      <th style={thLeft}>Certification Status</th>
                      <th style={thLeft}>Certifier Name</th>
                      <th style={thLeft}>Certifier Email</th>
                      <th style={thLeft}>Certifier Phone</th>
                      <th style={thRight}>Total Enrollment</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            ...tdBase,
                            textAlign: "center",
                            color: "#6c757d",
                            padding: "2.5rem 1rem",
                          }}
                        >
                          No LEAs match the selected status filter.
                        </td>
                      </tr>
                    ) : (
                      displayedData.map((row, index) => {
                        const name = formatCertifierName(row);
                        const baseBg = index % 2 === 0 ? "#ffffff" : STRIPE_BG;
                        return (
                          <tr
                            key={row.esiid}
                            className="report-table-row"
                            style={{ backgroundColor: baseBg }}
                            onMouseEnter={(e) =>
                              ((
                                e.currentTarget as HTMLTableRowElement
                              ).style.backgroundColor = HOVER_BG)
                            }
                            onMouseLeave={(e) =>
                              ((
                                e.currentTarget as HTMLTableRowElement
                              ).style.backgroundColor = baseBg)
                            }
                          >
                            <td style={tdBase}>{row.leaName}</td>
                            <td style={tdBase}>
                              <span style={nowrapSpan}>{row.vendorNumber}</span>
                            </td>
                            <td style={tdBase}>
                              <span
                                style={{
                                  ...badgeStyle,
                                  ...STATUS_COLORS[row.status],
                                }}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td style={name ? tdBase : tdMuted}>
                              {name || "—"}
                            </td>
                            <td style={row.certifierEmail ? tdWrap : tdMuted}>
                              {row.certifierEmail || "—"}
                            </td>
                            <td
                              style={
                                row.certifierPhoneNumber ? tdBase : tdMuted
                              }
                            >
                              {row.certifierPhoneNumber ? (
                                <span style={nowrapSpan}>
                                  {formatCertifierPhone(row)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td style={tdRight}>
                              {row.totalEnrollment.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    )}

                    {filteredData.length > 0 && (
                      <tr style={{ backgroundColor: "#ffffff" }}>
                        <td style={tdTotalLabel}>
                          Total ({filteredData.length}{" "}
                          {filteredData.length === 1 ? "LEA" : "LEAs"})
                        </td>
                        <td style={tdTotalLabel} />
                        <td style={tdTotalLabel} />
                        <td style={tdTotalLabel} />
                        <td style={tdTotalLabel} />
                        <td style={tdTotalLabel} />
                        <td style={tdTotalRight}>
                          {totalEnrollment.toLocaleString()}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={PAGE_SIZE}
                totalCount={filteredData.length}
                pageInput={pageInput}
                onPageInputChange={setPageInput}
                onCommitPageInput={commitPageInput}
                onFirst={() => setCurrentPage(1)}
                onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                onNext={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                onLast={() => setCurrentPage(totalPages)}
              />
            </div>

            {isFiltered && (
              <div
                className="mt-3 d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "0.9rem",
                  color: "#0f1923",
                }}
              >
                <LuListFilter
                  size={14}
                  style={{ flexShrink: 0, color: "var(--color-p2)" }}
                />
                <span>
                  Showing <strong>{selectedStatuses.size}</strong> of{" "}
                  <strong>{ALL_STATUSES.length}</strong> certification statuses.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
